import { ProgramEngagementModel, ProgramTask, ProgramTaskRole } from '@cibo/core'
import { ResponsiveDialogProps, logRequestError } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useProgramEngagementConfirmTask,
  useProgramModel,
  useSendEnrollmentMessage,
} from '../../../queries'
import { TaskMarkdown } from '../TaskMarkdown'

export const TaskDialog = ({
  engagement,
  onClose,
  onComplete,
  ownerName,
  taskId,
  userRole,
  ...modalProps
}: ResponsiveDialogProps & {
  onComplete: (taskId: ProgramTask['id']) => void
  engagement?: ProgramEngagementModel | null
  ownerName: string
  userRole: ProgramTaskRole
  taskId?: ProgramTask['id']
}) => {
  const { t } = useTranslation('@cibo/programs/TaskDialog')
  const program = useProgramModel({ programId: engagement?.programId })
  const taskContent = program.data?.taskContent(taskId)
  const task = program.data?.task(taskId)
  const confirmTask = useProgramEngagementConfirmTask()
  const sendEnrollmentMessage = useSendEnrollmentMessage()
  const { enqueueSnackbar } = useSnackbar()

  const canConfirm = engagement?.getTaskStatus(taskId)?.allowedActions?.includes('canModify')

  const onConfirm = useCallback(() => {
    switch (task?.rule.type) {
      case 'detailsLocked': {
        engagement?.id &&
          confirmTask
            .mutateAsync({
              id: engagement.id,
              taskId: task.id,
            })
            .then(() => taskId && onComplete(taskId))
        break
      }
      default: {
        taskId && onComplete(taskId)
      }
    }
  }, [taskId])

  const handleSendUserMessage = () => {
    engagement?.id &&
      sendEnrollmentMessage
        .mutateAsync({ workflowId: engagement?.id })
        .then(() => {
          enqueueSnackbar(t('notifySent'), {
            variant: 'success',
          })
          handleClose()
        })
        .catch(e => logRequestError(e))
  }

  const handleClose = useCallback(confirmTask.isLoading ? () => {} : onClose, [
    confirmTask.isLoading,
    onClose,
  ])

  return (
    <Dialog {...modalProps} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ paddingTop: 3 }}>{taskContent?.content?.title}</DialogTitle>
      <DialogContent>
        <TaskMarkdown color="text.secondary">
          {(task?.role.includes(userRole)
            ? taskContent?.content?.markdown?.[userRole]?.replace(/\{\{name\}\}/g, ownerName)
            : '') +
            '\n  \n' +
            (userRole === 'manager'
              ? t('sendMessageToUserInstructions', {
                  context: !task?.role.includes(userRole) ? 'only' : 'orConfirm',
                  ownerName: ownerName,
                  email: engagement?.ownedBy.userPrimaryEmail,
                })
              : '')}
        </TaskMarkdown>
        <Collapse in={confirmTask.isError}>
          <Alert severity="error" sx={{ marginTop: 3 }}>
            {confirmTask.error?.response?.data?.message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions sx={{ padding: 3 }}>
        <Button color="secondary" disabled={confirmTask.isLoading} onClick={handleClose}>
          {t('keepEditing')}
        </Button>
        {task?.role?.includes(userRole) && (
          <LoadingButton
            disabled={!canConfirm}
            loading={confirmTask.isLoading}
            onClick={onConfirm}
            variant={userRole === 'manager' ? 'outlined' : 'contained'}
          >
            {taskContent?.content.cta ?? t('confirm')}
          </LoadingButton>
        )}
        {userRole === 'manager' && (
          <LoadingButton
            loading={confirmTask.isLoading}
            onClick={handleSendUserMessage}
            variant="contained"
          >
            {taskContent?.content.notifyCta ?? t('notifyGrower')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
